import { createContext, useContext, useEffect, useState } from "react";
import api, { refreshSession, resetSessionExpired, setToken, tokenExpiresAt } from "@/lib/api";

const AuthCtx = createContext(null);
const RENEW_BEFORE_MS = 2 * 60 * 1000;

function useSessionKeepAlive(active) {
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const check = () => {
      if (document.visibilityState === "hidden") return;
      if (tokenExpiresAt() - Date.now() < RENEW_BEFORE_MS) refreshSession().catch(() => {});
    };
    const timer = setInterval(check, 30000);
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    check();
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [active]);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("ht:session-expired", onExpired);
    return () => window.removeEventListener("ht:session-expired", onExpired);
  }, []);

  useSessionKeepAlive(!!user);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setToken(data.access_token);
    resetSessionExpired();
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("ht_token");
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, setUser, login, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
